import { untrack } from "solid-js/web";
import { createActor, fromPromise } from "xstate";
import Editor from "~/js/editor";
import context from "~/js/editor";
import { getDefinitionOrAwait, getSyncNodeDefinition } from "~/js/graph/node-library";
import type { GraphNode as NodeType, Socket, SocketIn } from "~/js/graph/node-socket-connection";
import { Connection } from "~/js/graph/node-socket-connection";
import { applyModificationToString } from "~/js/graph/nodeModifier";
import { SocketCustomErrorMessageType } from "~/js/graph/socketCustomErrorMessage";
import * as assetStorage from "~/js/network/asset-storage";
import { mergeMlBase } from "~/js/network/mergeMlBase";
import { TaskApi } from "~/js/network/TaskApi";
import { imageCropGlobalId } from "~/js/nodes/local/logic/imageCrop/nodesGlobalIds";
import type { NodeDefinition, SocketDefinition } from "~/js/nodes/node-definition";
import type { NodeExecutor } from "~/js/nodes/node-executor";
import { NodeEvents } from "~/js/types/IEventorListners";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { calculateBoundariesOfNode, fitEditorToBoundary } from "~/js/utilities/fitToScreenUtils";
import { generate_UUID } from "~/js/utilities/UUID";
import { prepareSocketDefinitions, updateNodeWithFiles } from "~/js/workspace";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import type { HistoryResponseListItem } from "~/solidJs/entities/HistoryListEntity/api/HistoryApi";
import type { PiplineInfo } from "~/solidJs/entities/WorkspaceListEntity/helpers/workspaceTypes";
import { IndexApi } from "~/solidJs/pages/Index/data";
import {
	getPrivateMessageFromError,
	type IFileType,
	onNextFrame,
	pickRandomFromArray
} from "~/solidJs/shared/helpers";
import {
	type AudioSocketMetaData,
	type ICalculateCostDefinition,
	type INodeStartTrainingSchema,
	type ITaskSocket,
	type IUniversalTaskSocket,
	type IUserTemplateParams,
	type IWorkspaceFileSocket,
	type MediaSocketType,
	NodeVisibility,
	type SocketMetaData,
	type UserTemplateParamsHistory,
	type VideoSocketMetaData
} from "~/solidJs/shared/helpers/types/INodeDefinition";
import { ApiErrorHandlers } from "../api/baseApi";
import { FileApi } from "../api/fileApi";
import { queryClient } from "../api/queryClient";
import { CustomRuntimeError } from "../models/errors/runtimeErrors";
import { retryMachine, taskMonitoringMachine } from "../models/nodeStateMachine";
import { getAudioDurationFromFile } from "./audioHelpers";
import { sizeImageInLimits } from "./imageHelpers";
import { LimitQueue } from "./LimitQueue";
import { type IStateChageTracker, StateChageTracker } from "./stateMachineHelpres";
import {
	type ITaskDoneStatusResponse,
	type ITaskStatuses,
	type IUserSubscriptionPlans,
	type Position,
	type Resolution,
	TaskStatuses,
	UserSubscriptionPlanEnum
} from "./types";
import { getImageDimensionsFromFile } from "./validateImages";
import { getVideoMetadataFromFile } from "./videoHelpers";

export type IAddLocalNodeByGlobalId = {
	newOutputsFiles?: File[];
	globalId: string;
	position?: {
		x: number;
		y: number;
	};
	connections?: {
		fromNodeId: string;
		fromNodeSocketName: string;
		toNodeSocketName: string;
	}[];
};
export const findSocket = (socketName: string, node: NodeType) => {
	let socket = node.inputSocketGroup.socketHashMap.get(socketName);
	if (socket) return socket;
	const groups = node.paramSocketGroups.socketGroups;
	for (const group of groups) {
		socket = group.socketHashMap.get(socketName);
		if (socket) return socket;
	}
	const settingsGroup = node.paramSocketGroups.globalSettingsGroup;
	if (settingsGroup) {
		socket = node.paramSocketGroups.globalSettingsGroup.socketHashMap.get(socketName);
		if (socket) return socket;
	}
	return node.outputSocketGroup.socketHashMap.get(socketName) || null;
};
export const addNodeFromHistory = async (historyItem: HistoryResponseListItem) => {
	let seedParam: IUniversalTaskSocket | undefined;

	if (Array.isArray(historyItem.params.params)) {
		seedParam = historyItem.params.params.find((param) => param.name === "seed");
	} else {
		seedParam = historyItem.params.params.seed;
	}
	if (seedParam) {
		seedParam.isFixedSeed = true;
	}

	const mergedTemplateParams: IUserTemplateParams = {
		globalId: historyItem.id_global,
		inputSocketGroup: historyItem.params.inputs,
		params: historyItem.params.params,
		outputSocketGroup: [],
		name: getSyncNodeDefinition(historyItem.id_global).name,
		id: historyItem.params.id,
		taskID: historyItem.id,
		meta: {
			taskID: historyItem.id
		}
	};
	const node = await Editor.context.graph.pasteNodeByGlobalidAndParams(
		mergedTemplateParams,
		true,
		false,
		false
	);
	Editor.context.graph.toggleLastMove();
	const pos = getNodeCenterPosition(node);
	node.layout.setPosition(pos.x + Editor.context.graph.incrimentLastNodeOffset(), pos.y);
	node.loader.show();

	const outSocketToWorkspaceSocket = historyOutSocketToWorkspaceSocket(
		historyItem.params.outputs
	);
	const promises = [];
	for (const outSocket of outSocketToWorkspaceSocket) {
		promises.push(updateNodeWithFiles(node, outSocket, {}, -1));
	}
	const importFilesNode = Editor.context.graph.pasteImportFilesFromNodeInputs(
		historyItem,
		node.uuid,
		(importFilesNodeResult) =>
			Editor.context.graph.selectNodeList([node, importFilesNodeResult])
	);
	const importFilesNodeResult = await importFilesNode;
	if (!importFilesNodeResult) {
		Editor.context.graph.singleSelectNode(node);
	}
	await Promise.allSettled([...promises]);
	node.loader.hide();

	node.setIsModificationEnabled(true);
	return node;
};
export const historyOutSocketToWorkspaceSocket = (
	historySocketOuts: Record<string, ITaskSocket> | ITaskSocket[]
) => {
	if (!Array.isArray(historySocketOuts)) historySocketOuts = Object.values(historySocketOuts);
	const outputSocketWorkspace: IWorkspaceFileSocket[] = [];

	for (const historySocketOut of historySocketOuts) {
		let files: any;
		if (Array.isArray(historySocketOut.value)) {
			files = historySocketOut.value.map((id) => ({ fileId: id }));
		} else {
			if (!historySocketOut.id) continue;
			if (historySocketOut.type === "array") {
				files = historySocketOut.id.map((id) => ({ fileId: id }));
			} else {
				files = [
					{
						fileId: historySocketOut.id
					}
				];
			}
		}

		outputSocketWorkspace.push({
			name: historySocketOut.name,
			type: historySocketOut.type,
			value: files
		});
	}
	return outputSocketWorkspace;
};
export const getFirstImageIdFromHistoryOutputs = (
	outputs: UserTemplateParamsHistory["outputs"]
) => {
	if (!outputs) return null;
	let returnFileId: number | null = null;
	if (!Array.isArray(outputs)) {
		outputs = Object.values(outputs);
	}
	outerLoop: for (const output of outputs) {
		const type = output.type;
		if (type !== "image" && type !== "array") continue;
		if (Array.isArray(output.value)) {
			returnFileId = output.value[0] as number;
			break;
		}
		if (Array.isArray(output.id)) {
			for (const id of output.id) {
				returnFileId = id;
				break outerLoop;
			}
			continue;
		}

		returnFileId = output.id;
		break;
	}

	return returnFileId;
};

/**
 * @returns all image file ids from history item
 */
export const getAllImageIdsFromHistoryOutputs = (
	outputs: UserTemplateParamsHistory["outputs"]
): number[] => {
	const res: number[] = [];
	if (!outputs) return res;
	if (!Array.isArray(outputs)) {
		outputs = Object.values(outputs);
	}
	for (const output of outputs) {
		const type = output.type;

		const isVideo = output.name?.includes("video");
		if ((type !== "image" && type !== "array") || !output.id || isVideo) continue;
		if (Array.isArray(output.id)) {
			res.push(...output.id);
			continue;
		}
		if (Array.isArray(output.value)) {
			res.push(...(output.value.filter((v) => v !== null) as number[]));
			continue;
		}
		res.push(output.id);
		break;
	}

	return res;
};

export const getAllVideoIdsFromHistoryOutputs = (
	outputs: UserTemplateParamsHistory["outputs"]
): number[] => {
	const res: number[] = [];
	if (!outputs) return res;
	if (!Array.isArray(outputs)) {
		outputs = Object.values(outputs);
	}

	for (const output of outputs) {
		const type = output.type;

		const isVideo = type === "video" || output.name?.includes("video");

		if (!isVideo || !output.id) continue;
		if (Array.isArray(output.id)) {
			res.push(...output.id);
			continue;
		}
		if (Array.isArray(output.value)) {
			res.push(...(output.value.filter((v) => v !== null) as number[]));
			continue;
		}
		if (output.id !== null) {
			res.push(output.id);
		}
		break;
	}

	return res;
};

export const findSocketInParams = (
	socketGroup: IUserTemplateParams["params"] | IUserTemplateParams["inputSocketGroup"],
	socketName: string
) => {
	if (Array.isArray(socketGroup)) {
		for (const socket of socketGroup) {
			if (socket.name === socketName) return socket;
		}
	} else {
		if (socketName in socketGroup) return socketGroup[socketName];
	}
	return null;
};
export const mergeSocketWithNodeDef = (sockets: any[], socketsToMerge: SocketDefinition[]) => {
	const mergedArray: {
		name: string;
		value: string | number;
	}[] = [];
	for (const socket of sockets) {
		if (
			socket.type !== "text" &&
			socket.type !== "number" &&
			socket.type !== "enum" &&
			socket.type !== "boolean"
		)
			continue;
		const mergeSocket: SocketDefinition | undefined = socketsToMerge.find(
			(nodeDefSocket: SocketDefinition) => nodeDefSocket.name === socket.name
		);
		const pushName = mergeSocket?.getName() || socket.name;
		mergedArray.push({
			...socket,
			name: pushName
		});
	}
	return mergedArray;
};
export const addLocalNodeByGlobalId = async (prop: IAddLocalNodeByGlobalId) => {
	const { newOutputsFiles, position, globalId, connections } = prop;

	const originalDef = await getDefinitionOrAwait(globalId);
	const ImportNodeDef = Object.assign({}, originalDef);

	if (!ImportNodeDef) return;

	if (position) {
		ImportNodeDef.userTemplateParams = {
			position
		};
	}
	const createdNode = Editor.context.graph.addNode(
		ImportNodeDef,
		screen.width / 2,
		screen.height / 2,
		false
	);
	if (newOutputsFiles) {
		await (createdNode as any).handleInputFileChange?.(newOutputsFiles);
	}
	if (!position) {
		const width = createdNode.layout.domElement().offsetWidth;
		const height = createdNode.layout.domElement().offsetHeight;
		const { x, y } = context.context.graph.navigation.clientToGraphSpace(
			window.innerWidth / 2 - width / 2,
			window.innerHeight / 2 - height / 2
		);

		createdNode.layout.setPosition(x, y);
	}

	if (connections) {
		for (const outConnection of connections) {
			const fromGraphNode = Editor.context.graph.nodesHashMap.get(outConnection.fromNodeId);

			if (fromGraphNode) {
				const fromNodeSocket = fromGraphNode.inputSocketGroup.socketHashMap.get(
					outConnection.fromNodeSocketName
				);
				const toNodeSocket = createdNode.outputSocketGroup.socketHashMap.get(
					outConnection.toNodeSocketName
				);

				if (fromNodeSocket && toNodeSocket) {
					new Connection(fromNodeSocket, toNodeSocket, true, true, () => {}, true);
				}
			}
		}
	}
	return createdNode;
};

/** Record form of params for addRemoteNodeByGlobalId (key = param name, value = { name, value }) */
export type AddRemoteNodeParamsRecord = Record<
	string,
	{ name: string; value: string | number | boolean; isFixedSeed?: boolean }
>;

export type IAddRemoteNodeByGlobalId<globalId extends string | undefined> = {
	globalId?: globalId;
	definition?: NodeDefinition;
	params?:
		| ({
				name: string;
				value: string | number | boolean;
		  } & Record<string, any>)[]
		| AddRemoteNodeParamsRecord;
	inputs?:
		| ({
				name: string;
				value: string | number | boolean | null;
		  } & Record<string, any>)[]
		| Record<
				string,
				{
					name: string;
					value: string | number | boolean | null;
				}
		  >;
	newOutputsFiles?: File[];
	connections?: {
		fromNodeId: string;
		fromNodeSocketName: string;
		toNodeSocketName: string;
	}[];
	position?: {
		x: number;
		y: number;
	};
	onModelsFetch?: () => void;
	undoManagerIgnored?: boolean;
	uuid?: string;
	isStylesShowable?: boolean;
	toggleIsSavedStateIgnored?: boolean;
	isNeedToCancelAddSocket?: boolean;
	isOutputsArray?: boolean;
	meta?: Record<string, any>;
};
export function getNodeCenterPosition(node: NodeType): Position {
	const zoomScale = context.context.graph.navigation.getZoomScale();
	const width = node.layout.getWidth() * zoomScale;
	const height = node.layout.domElement().offsetHeight * zoomScale;
	const pos = context.context.graph.navigation.clientToGraphSpace(
		document.body.clientWidth / 2 - width / 2,
		document.body.clientHeight / 2 - height / 2
	);

	return pos;
}
export function centerNode(node: NodeType) {
	const pos = getNodeCenterPosition(node);

	node.layout.setPosition(pos.x, pos.y);
}
export async function addRemoteNodeByGlobalId<globalID extends string | undefined>(
	prop: IAddRemoteNodeByGlobalId<globalID>
) {
	const {
		globalId,
		definition,
		params,
		meta,
		inputs,
		newOutputsFiles,
		connections,
		position,
		onModelsFetch,
		undoManagerIgnored,
		uuid,
		toggleIsSavedStateIgnored,
		isNeedToCancelAddSocket,
		isOutputsArray
	} = prop;

	let nodeToCreate: any;

	if (!definition) {
		const node = await getDefinitionOrAwait(globalId);

		nodeToCreate = Object.assign({}, node);

		nodeToCreate.userTemplateParams = {
			params: params ? params : [],
			inputSocketGroup: inputs ? inputs : [],
			outputs: [],
			uuid: uuid || generate_UUID(),
			meta: meta
		};
	} else {
		const node = await getDefinitionOrAwait(definition.globalId);
		nodeToCreate = Object.assign({}, node);
		nodeToCreate.userTemplateParams = definition.userTemplateParams;
		if (meta) {
			nodeToCreate.useOutputViewer.meta = meta;
		}
		if (params) {
			nodeToCreate.userTemplateParams = {
				...nodeToCreate.userTemplateParams,
				params: { ...nodeToCreate.userTemplateParams?.params, ...params }
			};
		}
	}
	if (prop.isStylesShowable !== undefined) {
		nodeToCreate.isStylesShowable = prop.isStylesShowable;
	}

	if (nodeToCreate) {
		const createdNode = Editor.context.graph.addNode(
			nodeToCreate,
			screen.width / 2,
			screen.height / 2,
			false,
			true,
			undoManagerIgnored,
			toggleIsSavedStateIgnored
		);

		if (newOutputsFiles) {
			const outputSocketDefinitions = prepareSocketDefinitions(
				newOutputsFiles,
				undefined,
				!!isOutputsArray
			);
			if (!isNeedToCancelAddSocket)
				createdNode.outputSocketGroup.addSockets(outputSocketDefinitions);
			createdNode.updateOutputs([...outputSocketDefinitions]);
		}
		if (onModelsFetch) {
			createdNode.eventor.addListener(NodeEvents.MyModalsWasFetched, onModelsFetch);
		}
		if (position) {
			createdNode.layout.setPosition(position.x, position.y);
		} else {
			centerNode(createdNode);
		}

		if (connections) {
			for (const outConnection of connections) {
				const fromGraphNode = Editor.context.graph.nodesHashMap.get(
					outConnection.fromNodeId
				);

				if (fromGraphNode) {
					const fromNodeSocket = fromGraphNode.outputSocketGroup.socketHashMap.get(
						outConnection.fromNodeSocketName
					);
					const toNodeSocket = createdNode.inputSocketGroup.socketHashMap.get(
						outConnection.toNodeSocketName
					);

					if (fromNodeSocket && toNodeSocket) {
						if (toNodeSocket.getDataType() === fromNodeSocket.getDataType()) {
							new Connection(
								toNodeSocket,
								fromNodeSocket,
								true,
								true,
								() => {},
								true
							);
						}
					}
				}
			}
		}

		return createdNode;
	}
}

export const getOptionalFileNameFromHistoryTask = (task: UserTemplateParamsHistory) => {
	let optionalName: null | string = null;
	if (task.inputs) {
		const promptSocket =
			findSocketInParams(task.inputs, "text_prompt") ||
			findSocketInParams(task.inputs, "description");
		if (promptSocket) {
			optionalName = preparePromptToFileName(promptSocket.value as string);
		}
	}
	if (task.params) {
		const seedSocket = findSocketInParams(task.params, "seed");
		if (seedSocket) {
			optionalName += `_${seedSocket.value as number}`;
		}
	}
	return optionalName;
};

/**
 *
 * @param prompt {string}
 */
export function preparePromptToFileName(prompt: string) {
	return prompt.replace(/ /g, "_");
}

export const addOptionalNameToFileFromHistory = (
	file: IFileType,
	task: UserTemplateParamsHistory
) => {
	const optionalName = getOptionalFileNameFromHistoryTask(task);
	if (!optionalName) return;
	file.optionalName = optionalName;
};
export const addCropperToImageSocket = async (
	node: NodeType,
	socket: Socket,
	targetResolution: Resolution
): Promise<NodeType | null> => {
	const position = node.layout.getPosition();

	const createdNode = await addRemoteNodeByGlobalId({
		globalId: imageCropGlobalId,
		params: {
			height: {
				value: targetResolution.height,
				name: "height"
			},
			width: {
				value: targetResolution.width,
				name: "width"
			}
		},
		position
	});
	if (!createdNode) {
		return null;
	}
	node.layout.setPosition(position.x + createdNode.layout.getWidth() + 50, position.y);
	const heightParam = findSocket("height", createdNode);
	const widthParam = findSocket("width", createdNode);
	createdNode.addListener(NodeEvents.Ready, () => {
		onNextFrame(() => {
			if (heightParam) {
				heightParam.setValue(targetResolution.height);
				heightParam.eventor.invoke("change", targetResolution.height);
			}
			if (widthParam) {
				widthParam.setValue(targetResolution.width);
				widthParam.eventor.invoke("change", targetResolution.width);
			}
		});
	});

	const connections = [
		{
			from: { nodeUuid: createdNode.uuid, name: "resizedImage" },
			to: { nodeUuid: node.uuid, name: socket.name }
		}
	];
	const inputConnection = socket.connections[0];
	if (inputConnection) {
		connections.push({
			from: {
				nodeUuid: inputConnection.socketOut.node.uuid,
				name: inputConnection.socketOut.name
			},
			to: {
				nodeUuid: createdNode.uuid,
				name: "Image"
			}
		});
	}
	Editor.context.graph.pasteConnectionsFromTemplateParams({
		connections
	});
	onNextFrame(() => {
		if (heightParam) {
			heightParam.setValue(targetResolution.height);
		}
		if (widthParam) {
			widthParam.setValue(targetResolution.width);
		}
	});

	return createdNode;
};

/**
 * Helper function to get image dimensions for input sockets.
 * @param inputSocket The input socket being processed.
 * @param value The value obtained for the input socket.
 * @returns An object containing image dimensions if applicable, otherwise an empty object.
 */
const addInputImageMetadata = async (
	inputSocket: SocketIn,
	value: unknown
): Promise<SocketMetaData> => {
	if (inputSocket.getDataType() === "image" && value instanceof File) {
		const dimensions = await getImageDimensionsFromFile(value);
		if (!dimensions) return {};
		return { dimensions };
	}
	if (
		inputSocket.dataType === "array" &&
		Array.isArray(value) &&
		inputSocket.getDataType() === "image"
	) {
		const dimensionsArray = await Promise.all(
			value.map((file) => getImageDimensionsFromFile(file))
		);
		// Return an array of dimension objects for array inputs
		return {
			dimensions: dimensionsArray.filter((d): d is Resolution => d !== null)
		};
	}
	return {}; // Return empty object if not an image or image array
};

const addInputVideoMetadata = async (
	inputSocket: SocketIn,
	value: unknown
): Promise<VideoSocketMetaData> => {
	if (inputSocket.getDataType() === "video" && value instanceof File) {
		const videoMetadata = await getVideoMetadataFromFile(value);
		if (!videoMetadata) return {};
		return { duration: videoMetadata };
	}

	if (
		inputSocket.dataType === "array" &&
		Array.isArray(value) &&
		inputSocket.getDataType() === "video"
	) {
		const metadataArray = await Promise.all(
			value.map((file) => getVideoMetadataFromFile(file))
		);

		return {
			duration: metadataArray.filter((m): m is any => m !== null)
		};
	}
	return {};
};

const addInputAudioMetadata = async (
	inputSocket: SocketIn,
	value: unknown
): Promise<AudioSocketMetaData> => {
	if (inputSocket.getDataType() === "audio" && value instanceof File) {
		const duration = await getAudioDurationFromFile(value);
		if (duration === null) return {};
		return { duration };
	}

	if (
		inputSocket.dataType === "array" &&
		Array.isArray(value) &&
		inputSocket.getDataType() === "audio"
	) {
		const durations = await Promise.all(value.map((file) => getAudioDurationFromFile(file)));
		return {
			duration: durations.filter((d): d is number => d !== null)
		};
	}
	return {};
};

/**
 * @deprecated needs refactor
 * @description serialize node state to json
 * @param node
 * @param executor
 * @returns
 */
export const collectNodeDataForTask = async (
	node: NodeType,
	_executor: NodeExecutor,
	rewriteValues: Record<string, IFileType> = {}
) => {
	const jsonData: INodeStartTrainingSchema = {
		id: node.definition.serviceId,
		inputs: [],
		params: [],
		outputs: []
	};

	for (const socketOut of node.definition.outputs) {
		if (socketOut.dataType === "trigger") continue;
		const jsonOutput = jsonData.outputs.filter(
			(_jsonOutput) => _jsonOutput.name === socketOut.name
		)[0];

		if (jsonOutput) jsonOutput.value = [];
		else
			jsonData.outputs.push({
				name: socketOut.name,
				type: socketOut.dataType,
				value: ""
			});
	}

	const inputsSockets = node.inputSocketGroup.getSockets();
	for (const inputSocket of inputsSockets) {
		if (inputSocket.dataType === "trigger") continue;
		let value: any;
		if (!inputSocket.isEnabled || inputSocket.accessDisabled) {
			if (inputSocket.name in rewriteValues) {
				value = rewriteValues[inputSocket.name];
			} else {
				value = await inputSocket.getValue();
			}

			const limits =
				inputSocket?.options?.tariffLimits?.[getUserStore()?.group || "basic"] ||
				inputSocket?.options?.limits;
			if (limits && inputSocket.getDataType() === "image") {
				value = await scaleImageInLimitsFromCollectedJson({
					inputSocket,
					value,
					maxLimits: limits,
					minLimits: inputSocket?.options?.limits
				});
			}
		} else {
			if (inputSocket.dataType === "array") {
				const _limits = inputSocket?.options?.limits;
				if (
					inputSocket.options?.minItems &&
					inputSocket.options?.minItems > inputSocket.connections.length
				)
					throw `Connect at least ${inputSocket.options.minItems} images to "${inputSocket.definition.getName()}"`;
				if (
					inputSocket.options?.maxItems &&
					inputSocket.options.maxItems < inputSocket.connections.length
				) {
					throw `Input ${inputSocket.definition.getName()} has limit of ${inputSocket.options?.maxItems} ${inputSocket.options.itemType.dataType}s`;
				}
				for (let j = 0; j < inputSocket.connections.length; j++) {
					if (
						inputSocket.connections[j].socketOut.dataType !==
						inputSocket.options.itemType.dataType
					) {
						throw `Input ${inputSocket.definition.getName()} file number ${j + 1} needs to be ${
							inputSocket.options.itemType.dataType
						}`;
					}
				}
			}
			if (inputSocket.name in rewriteValues) {
				value = rewriteValues[inputSocket.name];
			} else {
				value = await inputSocket.getValue();
			}
			if (inputSocket?.options?.validateSimilar) {
				const similarToFind = inputSocket?.options?.validateSimilar;
				await validateSimizlarImageSockets(
					value,
					similarToFind.sockets,
					node,
					inputSocket,
					similarToFind.errorMessage,
					similarToFind.check_idx
				);
			}

			const limits =
				inputSocket?.options?.tariffLimits?.[getUserStore()?.group || "basic"] ||
				inputSocket?.options?.limits;
			if (limits && inputSocket.getDataType() === "image") {
				value = await scaleImageInLimitsFromCollectedJson({
					inputSocket,
					value,
					maxLimits: limits,
					minLimits: inputSocket?.options?.limits
				});
			}
			// second check for throwing error if we did not resize image correctly
			if (inputSocket?.options?.limits || inputSocket?.options?.tariffLimits) {
				if (limits?.hightLimit && limits?.widthLimit) {
					if (inputSocket.dataType === "array") {
						for (const imageFile of value) {
							const dm = await getImageDimensionsFromFile(imageFile);
							if (dm != null) {
								if (dm.height > limits.hightLimit || dm.width > limits.widthLimit) {
									throw `Check your image resolution.  It should be no more than ${limits.widthLimit} in width and ${limits.hightLimit} in height`;
								}
							}
						}
					} else {
						const dm = await getImageDimensionsFromFile(value);
						if (dm != null) {
							if (dm.height > limits.hightLimit || dm.width > limits.widthLimit) {
								throw `Check your image resolution.  It should be no more than ${limits.widthLimit} in width and ${limits.hightLimit} in height`;
							}
						}
					}
				}
			}
			if (
				(inputSocket.optional === false || inputSocket.Modifiedoptional === false) &&
				!value
			) {
				throw (
					inputSocket.getErrorMessage(SocketCustomErrorMessageType.EmptyNonOptional) ||
					`Input "${inputSocket.definition.getName()}" is empty`
				);
			}
			if (!inputSocket.optional && !value) {
				throw (
					inputSocket.getErrorMessage(SocketCustomErrorMessageType.EmptyNonOptional) ||
					`Input "${inputSocket.definition.getName()}" is empty`
				);
			}
			if (!value) value = "";
		}
		let metadata: SocketMetaData | undefined;
		try {
			// Get metadata (like image dimensions) using the helper function
			metadata = await addInputImageMetadata(inputSocket, value);
		} catch {}
		const inputJson: IUniversalTaskSocket = {
			name: inputSocket.name,
			type: inputSocket.dataType,
			optional: inputSocket.optional ? inputSocket.optional : null,
			isModified: inputSocket.Modifiedoptional === false || !inputSocket.isEnabled,
			value,
			meta: metadata // Assign the collected metadata
		};

		jsonData.inputs.push(inputJson);
	}

	for (const paramSocket of node.paramSocketGroups.getSockets()) {
		if (paramSocket.dataType === "modifier") continue;
		let param = {} as IUniversalTaskSocket;
		let value = await paramSocket.getValue();
		if (paramSocket.accessDisabled) {
			param = {
				name: paramSocket.name,
				type: paramSocket.dataType,
				isModified: true,
				value: value === undefined || value === "" ? null : value
			};
		} else if (!paramSocket.isEnabled && paramSocket.optional) {
			param = {
				name: paramSocket.name,
				type: paramSocket.dataType,
				isModified: true,
				value: null
			};
		} else {
			if (
				paramSocket.dataType === "number" &&
				paramSocket.options.random &&
				!paramSocket.optionalInfo.isFixedSeed
			) {
				const newSeed = paramSocket.dataEntry.generateRandomValue();
				paramSocket.dataEntry.setValue(newSeed);
				value = newSeed;
			}

			if (paramSocket.dataType === "text" && !value)
				param = {
					name: paramSocket.name,
					type: paramSocket.dataType,
					value: null
				};
			else {
				if (paramSocket.dataType === "enum") {
					// TODO: refactor
					// Because SD needs to pass text/number params to service
					// represented as enum in UI, we use this
					if (!Number.isNaN(Number(value))) value = Number.parseInt(value, 10);
				}
				param = {
					name: paramSocket.name,
					type: paramSocket.dataType,
					value
				};
			}
		}
		let metadata: SocketMetaData | undefined;
		try {
			// Get metadata (like image dimensions) using the helper function
			metadata = await addInputImageMetadata(paramSocket, value);
			param.meta = metadata;
		} catch {}

		jsonData.params.push(param);
	}
	modifyHiddenParams(jsonData, node);
	node.getAdditionalParamsForTask(jsonData);
	return jsonData;
};

const scaleImageInLimitsFromCollectedJson = async (props: {
	inputSocket: SocketIn;
	value: any;
	minLimits?: { minHeight?: number; minWidth?: number };
	maxLimits?: { hightLimit?: number; widthLimit?: number };
}) => {
	if (
		!props.minLimits?.minWidth &&
		!props.minLimits?.minHeight &&
		!props.maxLimits?.hightLimit &&
		!props.maxLimits?.widthLimit
	)
		return props.value;
	const maxHeight = props?.maxLimits?.hightLimit || Number.POSITIVE_INFINITY;
	const maxWidth = props?.maxLimits?.widthLimit || Number.POSITIVE_INFINITY;
	const minHeight = props?.minLimits?.minHeight || Number.NEGATIVE_INFINITY;
	const minWidth = props?.minLimits?.minWidth || Number.NEGATIVE_INFINITY;

	if (props.inputSocket.dataType === "array") {
		for (let i = 0; i < props.value.length; i++) {
			props.value[i] = await sizeImageInLimits(
				props.value[i],
				{
					heightLimit: maxHeight,
					widthLimit: maxWidth
				},
				{
					heightLimit: minHeight,
					widthLimit: minWidth
				}
			);
		}

		return props.value;
	}
	return sizeImageInLimits(
		props.value,
		{
			heightLimit: maxHeight,
			widthLimit: maxWidth
		},
		{
			heightLimit: minHeight,
			widthLimit: minWidth
		}
	);
};

// Helpers for image validation
const validateImageDimensions = (
	currentSizes: Resolution,
	targetSizes: Resolution,
	errorMessage?: string
): void => {
	if (currentSizes.height !== targetSizes.height || currentSizes.width !== targetSizes.width) {
		throw new CustomRuntimeError(
			errorMessage ||
				"Your image and Mask have different resolutions. Make sure they are of the same size and restart the node"
		);
	}
};

const validateFileAgainstFile = async (
	targetFile: IFileType,
	checkFile: IFileType,
	errorMessage?: string
): Promise<void> => {
	if (!targetFile || !checkFile) return;

	const [targetSizes, checkSizes] = await Promise.all([
		getImageDimensionsFromFile(targetFile),
		getImageDimensionsFromFile(checkFile)
	]);

	if (!targetSizes || !checkSizes) return;

	validateImageDimensions(checkSizes, targetSizes, errorMessage);
};

const getTargetFilesToValidate = (
	targetValue: IFileType,
	targetSocket: Socket,
	check_idx?: number[]
): IFileType[] => {
	const isArraySocket = targetSocket.dataType === "array";
	const hasIndices = check_idx && check_idx.length > 0;

	if (!isArraySocket) {
		return [targetValue];
	}

	const targetArray = Array.isArray(targetValue) ? targetValue : [targetValue];

	if (hasIndices) {
		// Проверяем только указанные индексы, пропускаем несуществующие файлы
		return check_idx!
			.filter((idx) => idx < targetArray.length && targetArray[idx])
			.map((idx) => targetArray[idx]);
	}

	// Проверяем все файлы из массива
	return targetArray.filter((file) => file);
};

const validateTargetFileAgainstSocket = async (
	targetFile: IFileType,
	checkSocket: SocketIn,
	errorMessage?: string
): Promise<void> => {
	const checkValue = await checkSocket.tryGetValue();
	if (!checkValue) return;

	if (checkSocket.dataType === "array") {
		const checkArray = checkValue as Array<IFileType>;
		for (const checkFile of checkArray) {
			await validateFileAgainstFile(targetFile, checkFile, errorMessage);
		}
	} else {
		await validateFileAgainstFile(targetFile, checkValue, errorMessage);
	}
};

const validateSimizlarImageSockets = async (
	value: IFileType,
	socketsToCheck: string[],
	node: NodeType,
	socket: Socket,
	errorMessage?: string,
	check_idx?: number[]
) => {
	// Получаем файлы для валидации из целевого сокета
	const targetFilesToValidate = getTargetFilesToValidate(value, socket, check_idx);
	if (targetFilesToValidate.length === 0) return;

	// Для каждого файла из целевого сокета проверяем его против всех сокетов из списка
	for (const targetFile of targetFilesToValidate) {
		for (const socketName of socketsToCheck) {
			const checkSocket = findSocket(socketName, node);
			if (!checkSocket) continue;

			await validateTargetFileAgainstSocket(targetFile, checkSocket, errorMessage);
		}
	}
};

const modifyHiddenParams = (jsonData: INodeStartTrainingSchema, node: NodeType) => {
	for (const modification of node.inputSocketGroupModifiers.getAllSocketsSortedModifications()) {
		const socketToFindName = modification.fromSocketName;
		const socketToModify = jsonData.inputs.find((socket) => socket.name === socketToFindName);
		if (!socketToModify) continue;
		socketToModify.value = applyModificationToString(
			socketToModify.value as string,
			modification
		);
	}
};

/**
 * @deprecated needs refactor
 * @description serialize Files objects by mutating prepeared json for task and synching files with server
 * @param jsonData
 * @param executor
 * @param node
 * @returns
 */
export const prepeareFileParamsForTask = async (
	jsonData: INodeStartTrainingSchema,
	_executor: NodeExecutor,
	node: NodeType
) => {
	for (let j = 0; j < jsonData.inputs.length; j++) {
		const input = jsonData.inputs[j];
		if (!input.value) continue;
		// If input have many connections
		if (input.type === "array") {
			// if (input.value.constructor.name == 'Array') {
			for (let s = 0; s < (input.value as any[]).length; s++) {
				const valueType = (input.value as any[])[s].type.split("/")[0];
				if (
					valueType === "audio" ||
					valueType === "image" ||
					valueType === "video" ||
					valueType === "object3d" ||
					valueType === "object3d_obj" ||
					valueType === "image_svg"
				) {
					const fileBlob = (input.value as any[])[s];
					const fileId = await FileApi.syncFileWithStorageAsync(fileBlob);
					(input.value as any[])[s] = fileId;

					// for (let i = 0; i < node.inputSocketGroup.getSockets().length; i++) {
					// 	const inputSocket = node.inputSocketGroup.getSockets()[i];
					// 	if (inputSocket.name == input.name) {
					// 		// Set the file ID to the node that we want compute
					// 		if (inputSocket.fileId) inputSocket.fileId.push(fileId);
					// 		else {
					// 			inputSocket.fileId = [];
					// 			inputSocket.fileId.push(fileId);
					// 		}
					// 		inputSocket.connections[s].socketOut.value.fileId = fileId;
					// 		// Set the file ID to the node that we got file from
					// 		// for (const connection of inputSocket.connections) {
					// 		// connection.socketOut.value.fileId = fileId
					// 		// }
					// 	}
					// }

					// TODO: Refactor
					// Set outputs from collected inputs to control backend multiple
					// file processing. In this version of service we control back output processing
					// from frontend. Also we can move this logic to backend
					// So we select the first input as main and required

					// jsonData.outputs[0].value = input.value

					// end
				}
				// TODO: Refactor
				// We set this parameter to 'array' and then to media type, because we need
				// it on backend side to create a right minio link for computing services
				input.type = valueType;
			}
			// If value is single file or string, etc
		} else {
			if (
				input.type === "audio" ||
				input.type === "image" ||
				input.type === "video" ||
				input.type === "object3d" ||
				input.type === "object3d_obj" ||
				input.type === "binary" ||
				input.type === "image_svg"
			) {
				const fileBlob = input.value;
				//@ts-expect-error
				const fileId = await FileApi.syncFileWithStorageAsync(fileBlob);
				input.value = fileId;

				// for (let i = 0; i < node.inputSocketGroup.getSockets().length; i++) {
				// 	const inputSocket = node.inputSocketGroup.getSockets()[i];
				// 	if (inputSocket.name === input.name) {
				// 		// Set the file ID to the node that we want compute
				// 		node.inputSocketGroup.getSockets()[i].fileId = fileId;

				// 		// Set the file ID to the node that we got file from
				// 		for (const connection of inputSocket.connections) {
				// 			connection.socketOut.value.fileId = fileId;
				// 		}
				// 	}
				// }
			}
		}
	}

	for (let j = 0; j < jsonData.params.length; j++) {
		const param = jsonData.params[j];
		if (
			param.type === "audio" ||
			param.type === "image" ||
			param.type === "video" ||
			param.type === "object3d" ||
			param.type === "object3d_obj" ||
			param.type === "image_svg"
		) {
			const fileBlob = param.value;
			//@ts-expect-error
			const fileId = await FileApi.syncFileWithStorageAsync(fileBlob);
			param.value = fileId;

			// Set the file ID to the node that we want compute
			for (let i = 0; i < node.paramSocketGroups.getSockets().length; i++) {
				//@ts-expect-error
				if (node.paramSocketGroups.getSockets()[i].name === input.name)
					node.paramSocketGroups.getSockets()[i].fileId = fileId;
			}
		}
	}
};
const globalIdToStaticBanner: Record<string, string> = {
	"Phygital Creator/phygc-rnd-dreambooth-stable-diff": "/public/Success_1.5.png",
	"Phygital Creator/phygc-rnd-lora-sdxl": "/public/SuccessXL.png",
	"Phygital Creator/phygc-rnd-dreambooth-stable-diff-2-0": "/public/Success_2.1.png",
	"Phygital Creator/phygc-rnd-lora-flux": "/public/Success_FLUX.png"
};

export const prepareFileToShowInNode = async (src: string) => {
	const processedOutputs = [];
	const response = await fetch(src);
	const blob = await response.blob();

	const filename = src.split("/").pop() || "unnamed.png";
	const file = new File([blob], filename, {
		type: blob.type
	});
	processedOutputs.push({
		name: "generated_image",
		type: "image",
		value: file
	});
	return processedOutputs;
};
/**
 * @deprecated needs refactor
 * @description pull result files and prepeare schema for nodes objects
 * @param jsonData
 * @param executor
 * @param node
 * @returns
 */
export async function prepeareCalculatedParamsForNode(
	task: ITaskDoneStatusResponse,
	nodeGlobalId: string,
	taskID: number
): Promise<any> {
	const processedOutputs = [];
	try {
		const outputs = task.outputs;

		if (nodeGlobalId in globalIdToStaticBanner) {
			return prepareFileToShowInNode(globalIdToStaticBanner[nodeGlobalId]);
		}

		for (const output of outputs) {
			if (output.type === "text") {
				processedOutputs.push(output);
				continue;
			}
			if (!output.id) {
				if (Array.isArray(output.value)) {
					const filesPromises = [];
					for (const fileId of output.value) {
						filesPromises.push(assetStorage.pullAssetViaAws(fileId));
					}
					const files = await Promise.all(filesPromises);
					//Todo: fix
					(output as any).type = files[0].type.split("/")[0];
					(output as any).value = files;
				} else {
					const file = await assetStorage.pullAssetViaAws(output.value);
					(output as any).value = file;
				}
				processedOutputs.push(output);
				continue;
			}
			if (Array.isArray(output.id)) {
				const filesPromises = [];
				for (const fileId of output.id) {
					filesPromises.push(assetStorage.pullAssetViaAws(fileId));
				}
				const files = await Promise.all(filesPromises);
				//Todo: fix
				(output as any).type = files[0].type.split("/")[0];
				(output as any).value = files;
			} else {
				const file = await assetStorage.pullAssetViaAws(output.id);
				(output as any).value = file;
			}
			processedOutputs.push(output);
		}
		return processedOutputs;
	} catch (err) {
		EventAnalytics.sendErrorInfo("error-pull-result-of-node", {
			message: getPrivateMessageFromError(err),
			taskID
		});
		console.error(`---- Error on pulling assets from storage: "${err}"`);
		throw err;
	}
}
export const prepeareParamsForAmplitude = (jsondData: INodeStartTrainingSchema) => {
	const map: Record<string, number | boolean | string | undefined | null> = {};
	for (const param of jsondData.inputs) {
		if (Array.isArray(param.value)) continue;
		map[param.name] = param.value as number | boolean | string | undefined | null;
	}
	for (const param of jsondData.params) {
		if (Array.isArray(param.value)) continue;
		map[param.name] = param.value as number | boolean | string | undefined | null;
	}
	return map;
};
export const findAndFocusToNode = (nodeUuid: string) => {
	const node = Editor.context.graph.nodesHashMap.get(nodeUuid);
	if (!node) return false;
	fitEditorToBoundary(calculateBoundariesOfNode(node));
	return true;
};
export const getNodesLimitsAcordGroup = (
	userGroup?: IUserSubscriptionPlans
): [string, number][] => {
	if (!userGroup) return [];
	const limitAcordGroup: Partial<Record<IUserSubscriptionPlans, [string, number][]>> = {
		advanced: [["Phygital Creator/phygc-rnd-stable-xl", 10]]
	};
	return limitAcordGroup[userGroup] || [];
};
export const constructWorkspaceLimitQueue = (
	graph: typeof Editor.context.graph,
	limits: [string, number][]
) => {
	if (!limits.length) return;
	const queue = new LimitQueue<string, NodeType>(
		(id) => graph.nodesHashMap.get(id)! as NodeType,
		(node) => node.globalId,
		(node, isBlocked) => node.toggleLimitDisability(isBlocked),
		(node) => node.isLimited,
		limits
	);

	graph.eventor.addListener("addNode", (uuid: string) => {
		queue.addRemoveEntities([uuid], true);
	});
	graph.eventor.addListener("deleteNode", (uuid: string) => {
		queue.addRemoveEntities([uuid], false);
	});
};
const getTaskCreatingErrorMessage = (error: unknown, onLimitExceeded?: () => void) => {
	if (error === ApiErrorHandlers.LimitExceededError) {
		onLimitExceeded?.();
		return ApiErrorHandlers.LimitExceededError;
	}
	return "Not enough credits on your account.";
};
const getErrorMessageAccordErrorStatus = (status: ITaskStatuses, error_message?: string) => {
	if (error_message) {
		return error_message;
	}
	if (status === TaskStatuses.Error) {
		return `Please reload the page to make sure you're using the up-to-date version. If the error keeps appearing, use Bug report feature in the right down page corner`;
	}
	if (status === TaskStatuses.Error_cuda) {
		return `Can't get memory for processing. May be too many tasks are now run at the same time, just try to relaunch this node in a sec.`;
	}
	if (status === TaskStatuses.Error_exec_timeout) {
		return "Looks like your generation took too long. You may be in a service queue. Please try again, or try lowering the input/output size or quality.";
	}
	if (status === TaskStatuses.Error_params) {
		return `Please reload the page to make sure you're using the up-to-date version. If the error keeps appearing, use Bug report feature in the right down page corner`;
	}
	if (status === TaskStatuses.Canceled) {
		return "Error while creating task, please restart generation";
	}
	return "Unkown error happend";
};
const getStaledErrorMessageAccordErrorStatus = (status: ITaskStatuses) => {
	if (status === TaskStatuses.Pending) {
		return "Lots of requests right now — give it a moment and try again.";
	}
	if (status === TaskStatuses.Preparing) {
		return "Node preparing is too long";
	}
	if (status === TaskStatuses.Running) {
		return "Node computing is too long";
	}
	return "Uknown status computing is too long";
};
export const createNodePullingMachine = (
	globalId: string,
	onRun: () => void,
	onWaitingForLaunch: () => void,
	onTaskCreated: (taskId: number) => void,
	onTaskStaled: (message: string) => void,
	onPendingStatus: (position: number) => void,
	onError: (message: string, errorCode?: string) => void,
	onRetryStart: () => void,
	taskData: INodeStartTrainingSchema,
	taskStatusesToTimeout: Record<string, number> = {
		pending: 1200000 * 2000,
		computing: 1200000 * 1000,
		prepearing: 900000
	},
	executionStartTs: number,
	cancelController: AbortController,
	actors: {
		createTask?: typeof TaskApi.postStartTraining;
		getTaskStatus: typeof TaskApi.getTaskStatus;
		tracker: NodeStateTracker;
	},
	onLimitExceeded?: () => void
): Promise<ITaskDoneStatusResponse> => {
	let resolveRet: (value: ITaskDoneStatusResponse) => void;
	let rejectRet: (error: string) => void;
	const retPromise = new Promise<ITaskDoneStatusResponse>((resolve, reject) => {
		resolveRet = resolve;
		rejectRet = reject;
	});
	const PostTraining = actors?.createTask || TaskApi.postStartTraining;
	const GetTaskStatus = actors?.getTaskStatus || TaskApi.getTaskStatus;
	const actor = createActor(
		taskMonitoringMachine.provide({
			actors: {
				createTask: fromPromise(
					async ({
						input
					}: {
						input: {
							schema: INodeStartTrainingSchema;
							signal: AbortSignal;
						};
					}) => {
						return PostTraining(input.schema, input.signal);
					}
				),
				getTaskStatus: fromPromise(
					async ({ input }: { input: { taskId: number; signal: AbortSignal } }) => {
						return GetTaskStatus(input.taskId, input.signal);
					}
				),
				retryMachine: retryMachine.provide({
					actors: {
						fetchImpl: fromPromise(async ({ input }: { input: number }) => {
							return GetTaskStatus(input);
						})
					}
				})
			},
			actions: {
				onTaskStatusChange({ context }) {
					const status = context.taskStatus?.status;
					const lastStatus = context.lastTaskStatus;
					if (lastStatus) {
						// we use last status so newStage relativeTs will include status itself e.g: computing -> 'needed ts diff' -> done
						actors.tracker.trackStateChange({ newState: lastStatus });
					}
					const position = context.taskStatus?.position;
					const taskId = context.taskId;
					const taskCreatingTs = context.taskCreatingTs;
					if (status === TaskStatuses.Pending) {
						EventAnalytics.firstPendingTaskStatus(
							globalId,
							taskCreatingTs,
							taskId || 0,
							position || 0,
							Date.now() - taskCreatingTs
						);
					}
					if (status === TaskStatuses.WaitingForLaunch) {
						onWaitingForLaunch();
						return;
					}
					if (status === TaskStatuses.Running) {
						onRun();
						return;
					}
				},
				onTaskGetStatusError({ context }, error: unknown) {
					EventAnalytics.sendErrorInfo("error-get-status", {
						message: getPrivateMessageFromError(error),
						globalId,
						taskId: context.taskId || 0
					});
				},
				onTaskPositionChange({ context }) {
					if (context.taskStatus?.status !== TaskStatuses.Pending) {
						return;
					}
					onPendingStatus(context.taskStatus.position);
				},
				onTaskCreatingSucsess({ context }) {
					const taskId = context.taskId;

					const createTaskTs = Date.now();
					if (!taskId) return;
					EventAnalytics.createTask(
						globalId,
						createTaskTs,
						taskId!,
						createTaskTs - executionStartTs,
						prepeareParamsForAmplitude(taskData)
					);
					onTaskCreated(taskId);
				},
				onTaskCreatingError(_, error: unknown) {
					const message = getTaskCreatingErrorMessage(error, onLimitExceeded);
					EventAnalytics.sendErrorInfo("error-task-creating", {
						message: getPrivateMessageFromError(error),
						globalId
					});
					onError(message, TaskStatuses.Error);
					rejectRet(message);
				},
				onTaskRetryStart() {
					onRetryStart();
				},
				onTaskRetrySucsess({ context }) {
					const status = context.taskStatus?.status;
					if (status !== "running") return;
					onRun();
				},
				onTaskRetryError({ context }, error: unknown) {
					const status = context.taskStatus?.status;
					const taskId = context.taskId;
					EventAnalytics.sendErrorInfo("error-task-retry", {
						message: "Retry limit exceeded",
						lastError: getPrivateMessageFromError(error),
						globalId,
						taskId: taskId!,
						...(status ? { taskStatus: status } : {})
					});
					const message =
						"Task retry error. Please check your connection stability and relaunch the node";
					onError(message, status || TaskStatuses.Error);
					rejectRet(message);
				},
				onTaskErroredStatus({ context }) {
					const status = context.taskStatus?.status;
					let error_message: string | undefined;
					if (context.taskStatus && "error_message" in context.taskStatus) {
						error_message = context.taskStatus.error_message;
					}
					const taskId = context.taskId;
					EventAnalytics.sendErrorInfo("error-task", {
						message: "Task failed with error status",
						globalId,
						taskStatus: status!,
						taskId: taskId!
					});

					const message = getErrorMessageAccordErrorStatus(
						status || "error",
						error_message
					);

					onError(message, status || TaskStatuses.Error);
					rejectRet(message);
				},
				onTaskStaled({ context }) {
					actors.tracker.trackStateChange({
						newState: TASK_TRACK_STAGES.staled
					});
					const status = context.taskStatus?.status;
					const taskId = context.taskId;
					EventAnalytics.sendErrorInfo("error-task-staled", {
						message: "Task is too long have status",
						globalId,
						taskStatus: status!,
						taskId: taskId!
					});
					const message = getStaledErrorMessageAccordErrorStatus(status || "error");
					onTaskStaled(message);
					rejectRet(message);
				}
			}
		}),
		{
			input: {
				errorTaskStatuses: {
					error: true,
					canceled: true,
					error_exec_timeout: true,
					error_cuda: true,
					error_params: true
				},
				taskData,
				taskStatusesToTimeout,
				abortController: cancelController
			}
		}
	);
	actor.subscribe((snapshot) => {
		if (snapshot.status === "done") {
			resolveRet(snapshot.output as ITaskDoneStatusResponse);
		}
	});
	cancelController.signal.addEventListener("abort", () => {
		actor.stop();
		const snapshot = actor.getSnapshot();
		actors.tracker.trackStateChange({
			newState: snapshot.context.taskStatus?.status || "new"
		});
		actors.tracker.trackStateChange({ newState: TASK_TRACK_STAGES.cancel });
		rejectRet("Canceled");
	});
	actor.start();
	return retPromise;
};

type SeriliazedSoketOut = {
	name: string;
	type: string;
	value: { fileId: number }[];
	optionalInfo: Record<string, any>;
};
export const serializeNodeOutputs = async (node: NodeType) => {
	const nodeValues: SeriliazedSoketOut[] = [];
	const outSockets = node.outputSocketGroup.getSockets();
	const promises: Promise<SeriliazedSoketOut | null>[] = outSockets.map(async (outputSocket) => {
		if (outputSocket.dataType === "trigger") return null;
		let _value = outputSocket.value;
		const outputSocketName = outputSocket.name.split("__")[0];

		if (
			_value instanceof File &&
			(node.globalId === "Import Files" ||
				node.definition.isNeedToSynchOutputs ||
				!(_value as IFileType).fileId)
		) {
			const id = await FileApi.syncFileWithStorageAsync(_value);
			_value = { fileId: id };
		}
		if (_value !== undefined) {
			const fileId = _value?.fileId;
			if (fileId !== undefined) {
				_value = { fileId: fileId };
			}
		}
		if (!_value) {
			_value = outputSocket.getOriginalWorkspaceIds();
			if (!_value) return null;
		}
		return {
			name: outputSocketName,
			type: outputSocket.dataType,
			value: [_value],
			optionalInfo: {
				...(outputSocket.dataType === "object3d" ? outputSocket.optionalInfo : {}),
				...(await outputSocket.customSerializationData(true)),
				originalWorkspaceIds: _value
			}
		};
	});

	const values = await Promise.allSettled(promises);
	const nodeValueIndexNameMap: Record<string, number> = {};
	for (let i = 0; i < values.length; i++) {
		const resolved = values[i];
		if (resolved.status === "rejected") {
			throw resolved.reason;
		}
		if (!resolved || !resolved?.value) continue;
		const socket = resolved.value;
		const outputSocketName = socket.name;
		const searchIndex = nodeValueIndexNameMap[outputSocketName];
		if (searchIndex !== undefined) {
			nodeValues[searchIndex].value.push(socket.value[0]);
			continue;
		}
		nodeValues.push(socket);
		nodeValueIndexNameMap[socket.name] = nodeValues.length - 1;
	}
	return nodeValues;
};
/**
 * Task stages
 * @category Task
 */
export const TASK_TRACK_STAGES = {
	start: "start",
	/** Successful parameter validation */
	validateParams: "validateParams",
	/** Successful push of task files to the server */
	pushFiles: "pushFiles",
	/** Successful creation of the task on the server */
	taskCreated: "taskCreated",
	new: "new",
	preparing: "preparing",
	pending: "pending",
	running: "running",
	waiting_for_launch: "waiting_for_launch",
	done: "done",
	error: "error",
	/** Task have canceled status for some reason */
	canceled: "canceled",
	error_exec_timeout: "error_exec_timeout",
	error_cuda: "error_cuda",
	/** Node stayed in one state for too long */
	staled: "staled",
	error_params: "error_params",
	/** We download files after computing */
	pullFiles: "pullFiles",
	/** User cancele the task */
	cancel: "cancel",
	/** Successful computation after all stages */
	success: "success",
	/** General exception for all occurred errors in the task */
	exception: "exception",
	/** Finaly stage, used for logging prev last stage eg cancel,success,exeption */
	finaly: "finaly"
} as const;

export type NodeStateTracker = IStateChageTracker<typeof TASK_TRACK_STAGES>;
export const constructNodeStateChangeTracker = (props: { globalId: string }) => {
	const additionalInfo = {
		globalId: props.globalId,
		taskId: -1
	};
	const tracker = new StateChageTracker({
		stages: TASK_TRACK_STAGES,
		trackImpl(trackProps) {
			const trackInfo = { ...trackProps, ...additionalInfo };
			EventAnalytics.trackTaskStateChanges(trackInfo);
		},
		initStage: TASK_TRACK_STAGES.start
	});
	const setTaskId = (id: number) => {
		additionalInfo.taskId = id;
	};
	return { tracker, setTaskId };
};

export const serializeConnectedNodes = async (node: NodeType) => {
	const connectedNodes = Editor.context.graph.findAlltConnectedNodes([node]);
	connectedNodes.push(node);
	const serializedNodes = await Editor.context.graph.serializeNodesAndThierConnections(
		connectedNodes,
		false
	);
	return serializedNodes;
};

export const sendConnectedNodes = async (taskId: number, node: NodeType) => {
	const pipline = await constructPiplineInfo(node);
	TaskApi.postTaskConnectedGraph(taskId, pipline);
};

export const constructPiplineInfo = async (node: NodeType): Promise<PiplineInfo> => {
	const nodes = await serializeConnectedNodes(node);
	return {
		nodes,
		executedNodeUuid: node.uuid
	};
};

/**
 * @description is node hiden/disabled by backend limits
 */
export const isNodeDisabled = (node: NodeDefinition) => {
	return node.isAvailable === false;
};

/**
 * @description is node hiden by backend limits from sidebar
 */
export const isNodeSidebarHidden = (node?: NodeDefinition) => {
	if (!node) {
		return true;
	}
	// node is visible
	if (node.isVisible === NodeVisibility.Visible) {
		return false;
	}
	const userGroup = untrack(() => getUserStore()?.group);
	// node is visible if user group is superuser
	if (
		node.isVisible === NodeVisibility.SuperUser &&
		userGroup === UserSubscriptionPlanEnum.Superuser
	) {
		return false;
	}
	// node is hidden
	return true;
};

/**
 * @description is node hiden by backend limits from cntx menu
 * node in context menu is hidden only when it's avalible to superusers only, or explicitly hidden by setting state to Invisible
 */
export const isNodeCntxMenuHidden = (node?: NodeDefinition) => {
	if (!node) {
		return true;
	}
	const userGroup = untrack(() => getUserStore()?.group);
	if (
		node.isVisible === NodeVisibility.SuperUser &&
		userGroup !== UserSubscriptionPlanEnum.Superuser
	) {
		return true;
	}
	if (isNodeUnavalibleInGraphSpace(node)) {
		return true;
	}
	if (node.isVisible === NodeVisibility.Invisible) {
		return true;
	}
	return false;
};

export const isNodeUnavalibleInGraphSpace = (node?: NodeDefinition) => {
	if (!node) return false;

	// Проверка на отключенную ноду + проверка тарифа(isAvailable: false, visible: invisible)
	// Такие ноды недоступны, но есть в проектах/темплейтах
	return (
		node.isAvailable === false &&
		(node.isVisible === NodeVisibility.Old || node.isVisible === NodeVisibility.Invisible)
	);
};

const waitingTexts = [
	"Warming up AI... Coffee in progress.",
	"AI coming to life... Ready for duty!",
	"AI downloading a fresh cup of inspiration.",
	"AI initializing... Coffee first, code second.",
	"AI powering on... Ready to create some pixel magic!",
	"AI initializing... Time to unleash creativity!"
];
export const getWaitingText = () => {
	return pickRandomFromArray(waitingTexts);
};
const timeoutTexts = [
	"AI is trying to find the meaning of life... This might take a while.",
	"AI is solving time travel paradoxes. Please wait.",
	"AI is decoding the secrets of the universe. Patience is key!",
	"Meditating with AI gurus for the ultimate truth.",
	"Recounting the atoms of the Universe... The answer will be 42.",
	"Loading... Searching for the meaning of life, the Universe, and everything else.",
	"Please wait... 42% complete. From here, we're just having fun."
];
export const getTimeoutText = () => {
	return pickRandomFromArray(timeoutTexts);
};

export const getTipComputingText = () => {
	const texts = [
		"Use 'Improve' button in prompt to automatically enhance your descriptions.",
		"Use Ctrl+C/V to copy nodes, Ctrl+Enter to launch, Ctrl+Z to undo actions.",
		"Hover over images for quick Upscale, Inpaint, Variations, and Save.",
		"Connect nodes together to create automated workflows: generate → enhance → upscale.",
		"Change Seed number in settings for different results with same prompt parameters.",
		"Click 'Save project' to save your workflows and Share them using Share button.",
		"Use vertical (9:16) resolutions for portraits, horizontal (16:9) for ad banners, square (1:1) for social images.",
		"Add 'blurry, ugly, deformed, poor quality' to negative prompts for cleaner results.",
		"Try 'Midjourney Style' LoRA in FLUX for artistic enhancement or 'Detailer' for detalization.",
		"Resize your image using FLUX Outpainting: pan, zoomout or set desired aspect ratio",
		"Use Automatic Image Mask node for masks: describe objects with text instead of drawing.",
		"Train LoRA with 7-15 images for personalized avatars and branded style content creation.",
		"Use Kling or Runway for quick 5-second animations.",
		"Convert product images to 3D with '3D from Image', rotate and render.",
		"Convert vector illustrations to SVG using Vectorizer for infinite scalability.",
		"Use Compose Node to combine up to 6 images before final AI processing.",
		"Set 'Number of images' to 2 for multiple variations per run.",
		"All generations auto-save in History - easily return to any previous result.",
		`Use Gemini or FLUX with quoted text: '3d text "PHYGITAL"' for readable text images.`,
		"Prompt Generator and Extend makes optimized English prompts from any language."
	];
	return `Tip: ${pickRandomFromArray(texts)}`;
};

export function isFetchesExternalModels(definition: NodeDefinition) {
	return definition.isNeedToFetchLoras || definition.isNeedToFetchModels;
}

export function isMidjourneyVariate(node: NodeType) {
	node.globalId === "Phygital Creator/phygc-rnd-midjourney";
}

export function getPositionToTheRightWithPadding(node: NodeType) {
	const width = node.layout.getWidth();
	const pos = node.layout.getPosition();

	return {
		x: pos.x + width + 100,
		y: pos.y
	};
}

export async function refetchNodeAvalibility() {
	await queryClient.fetchQuery(IndexApi.getFetchCloundNodeListOptions());
	const airtableItems = await mergeMlBase();
	for (const node of Editor.context.graph.nodes) {
		const airtableNode = airtableItems.find((item) => item.globalId === node.globalId);
		if (!airtableNode || !airtableNode.node) continue;
		node.executuionBlock.toggleTarfiLimited(!airtableNode.node.isAvailable);
	}
}

export const getShouldShowLeaveWarning = () => !Editor.context.graph.getIsSaved;

const socketToCostSocket = (socket: SocketIn) => {
	return {
		name: socket.name,
		value: socket.tryGetValue(),
		type: socket.getDataType()
	};
};

const socketImageToCostSocketIn = async (socket: SocketIn) => {
	return {
		name: socket.name,
		value: null,
		type: socket.getDataType(),
		meta: { ...(await addInputImageMetadata(socket, socket.tryGetValue())) }
	};
};

const socketVideoToCostSocketIn = async (socket: SocketIn) => {
	console.log("socketVideoToCostSocketIn", socket);
	return {
		name: socket.name,
		type: socket.getDataType(),
		meta: { ...(await addInputVideoMetadata(socket, socket.tryGetValue())) }
	};
};

const socketAudioToCostSocketIn = async (socket: SocketIn) => {
	console.log("socketVideoToCostSocketIn", socket);
	return {
		name: socket.name,
		type: socket.getDataType(),
		meta: { ...(await addInputAudioMetadata(socket, socket.tryGetValue())) }
	};
};

const MEDIA_SOCKET_HANDLERS: Record<
	MediaSocketType,
	(socket: SocketIn) => Promise<{ name: string; type: string; value?: null; meta?: any }>
> = {
	image: socketImageToCostSocketIn,
	video: socketVideoToCostSocketIn,
	audio: socketAudioToCostSocketIn
};

const isMediaSocket = (dataType: string): dataType is MediaSocketType => {
	return dataType === "image" || dataType === "video" || dataType === "audio";
};

export const prepeareCostDefinition = async (node: NodeType): Promise<ICalculateCostDefinition> => {
	const inputs = await Promise.allSettled(
		node
			.getSocketsIn()
			.filter((socket) => {
				const dataType = socket.getDataType();
				return isMediaSocket(dataType);
			})
			.map((socket) => {
				const dataType = socket.getDataType() as MediaSocketType;
				const handler = MEDIA_SOCKET_HANDLERS[dataType];
				return handler(socket);
			})
	);
	const out: ICalculateCostDefinition = {
		id: node.definition.serviceId,
		inputs: inputs.filter((input) => input.status === "fulfilled").map((input) => input.value),
		params: node
			.getSocketsParam()
			.filter((socket) => socket.name !== "seed")
			.map(socketToCostSocket),
		outputs: []
	};
	return out;
};

export const getNodeBasePrice = (globalId: string) => {
	const definition = getSyncNodeDefinition(globalId);
	if (!definition) return null;
	return definition.defaultPrice;
};

export const getNodeBasePriceFromQuery = (
	query: Record<string, { definition: NodeDefinition }>,
	globalId: string
) => {
	const definition = query[globalId];
	if (!definition) return null;
	return definition.definition.defaultPrice;
};
